'use client'
import { useState } from "react";
import Image from "next/image";
import { Grid, GridItem } from "../components/grid";
import { HeroHeading, HeroDescription, ScrollDownButton, EventCard } from "./components";
import heroGirls from "@/assets/image/hero-grirls.png";
import stickerSquareSimple from "@/assets/svgs/tami-square-simple.svg";
import { HOME_COPY } from "../home.copy";

/**
 * Os quatro cards que flutuam ao redor da foto.
 *
 * Posições, larguras e ângulos vêm do `group-stickers-green-collors.svg` que
 * eles substituem: o viewBox era 972×658 e cada card media 306×78, sempre
 * 31.5% da largura do palco. Por isso o palco abaixo carrega a mesma proporção
 * do viewBox — sem ela as porcentagens escorregam junto com a caixa da foto.
 *
 * Os dois da direita ancoram por `right` e não por `left`: em telas médias o
 * card fica proporcionalmente mais largo para o texto continuar legível, e
 * ancorado pela esquerda ele cresceria para fora do palco.
 *
 * O texto de cada card (título e horário) vem de `HOME_COPY.hero.events`, na
 * mesma ordem desta lista.
 *
 * `depth` é o `translateZ` de cada card. Com `preserve-3d` no palco, a mesma
 * rotação do grupo desloca cada profundidade num ritmo diferente — o parallax
 * sai da geometria, sem cálculo por card no mousemove.
 */
const HERO_EVENTS = [
  { side: "left", offset: "5.3%", top: "1.1%", rotate: "2.93deg", depth: 40 },
  { side: "left", offset: "1.1%", top: "18.7%", rotate: "-1.38deg", depth: 28 },
  { side: "right", offset: "1.1%", top: "69.2%", rotate: "-1.67deg", depth: 16 },
  { side: "right", offset: "6.9%", top: "85.7%", rotate: "2.25deg", depth: 4 },
] as const;

export function BannerHero() {
  const copy = HOME_COPY.hero;
  const [transform, setTransform] = useState('');
  const THRESHOLD = 15;

  const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (prefersReducedMotion()) return;

    const { clientX, clientY, currentTarget } = e;
    const { clientWidth, clientHeight } = currentTarget;
    const offsetLeft = currentTarget.getBoundingClientRect().left;
    const offsetTop = currentTarget.getBoundingClientRect().top;

    const horizontal = (clientX - offsetLeft) / clientWidth;
    const vertical = (clientY - offsetTop) / clientHeight;
    const rotateX = (THRESHOLD / 2 - horizontal * THRESHOLD).toFixed(2);
    const rotateY = (vertical * THRESHOLD - THRESHOLD / 2).toFixed(2);

    // O `scale3d` volta: era ele que fazia o conjunto crescer sob o mouse. O
    // que borrava não era a ampliação em si, e sim o `filter: drop-shadow`
    // desta camada, que forçava rasterizar tudo em bitmap antes de escalar.
    setTransform(
      `perspective(${clientWidth}px) rotateX(${rotateY}deg) rotateY(${rotateX}deg) scale3d(1.05, 1.05, 1.05)`
    );
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    const { clientWidth } = e.currentTarget;
    setTransform(
      `perspective(${clientWidth}px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`
    );
  };

  return (
    <section className="w-full min-h-dvh bg-tami-default flex items-center relative">
      <Grid className="py-16 px-4 md:py-24 md:px-8">
        {/* Conteúdo principal do Hero - Lado Esquerdo */}
        <GridItem mobile={4} tablet={4} desktop={6}>
          <div className="flex flex-col gap-8">

            <span>
            <Image
                  src={stickerSquareSimple}
                  alt={copy.stickersAlt}
                  width={50}
                  height={50}
                  className="object-contain hidden lg:block"
                />
            </span>


            {/* Título principal com destaques */}
            <HeroHeading className="md:text-nowrap font-bold">
              {copy.headingLead}
              <br />
              <HeroHeading.Highlight >{copy.headingHighlights[0]}</HeroHeading.Highlight> <br />
              <HeroHeading.Highlight>{copy.headingHighlights[1]}</HeroHeading.Highlight> <br />
              <HeroHeading.Highlight>{copy.headingHighlights[2]}</HeroHeading.Highlight>
            </HeroHeading>

            {/* Descrição */}
            <HeroDescription>
              {copy.description}
            </HeroDescription>

            {/* Botão de Scroll Down */}
            <div className="mt-4 items-center justify-center md:justify-start flex w-full">
              <ScrollDownButton />
            </div>
          </div>
        </GridItem>

        {/* Imagem Hero com os cards flutuando ao redor - Lado Direito */}
        <GridItem mobile={4} tablet={4} desktop={6} className="flex items-center justify-center">
          {/* A mesma cena em qualquer largura. No mobile o container troca a
              altura mínima pela própria proporção da foto: ela preenche a
              coluna e o palco encolhe junto, então o desenho fica menor e não
              diferente. */}
          <div
            className="relative w-full aspect-[895/672] md:aspect-auto md:h-full md:min-h-[400px] lg:min-h-[500px] cursor-pointer"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            {/* Imagem de fundo */}
            {/* Esta é a imagem do LCP da home, e as três props andam juntas:
                `fetchPriority="high"` substitui o `priority` (depreciado no
                Next 16) e dá a dica de prioridade que faltava ao pedido;
                `loading="eager"` é obrigatório junto, porque sem `priority` a
                imagem cai no lazy padrão — medimos 58-107ms de atraso só por
                isso; e `sizes` impede o `fill` de pedir a variante de 3840px,
                já que sem ele o navegador assume `100vw` em qualquer tela. */}
            <Image
              src={heroGirls}
              alt={copy.photoAlt}
              fill
              sizes="(max-width: 768px) 100vw, 50vw"
              className="object-contain"
              fetchPriority="high"
              loading="eager"
            />

            {/* Palco dos cards - MAIOR que a foto, na proporção do desenho original */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-visible">
              {/* Sem `shrink-0` de propósito: como filho de flex, o palco
                  encolhe de 150% para a largura da caixa da foto — que é
                  justamente onde o `object-contain` do SVG antigo parava, já
                  que a arte era limitada pela largura e não pela altura. É o
                  que mantém os cards onde sempre estiveram. */}
              <div
                className="relative w-[150%] aspect-[972/658]"
                style={{
                  transformStyle: 'preserve-3d',
                  transform: transform || 'rotateX(0deg) rotateY(0deg)',
                  transition: 'transform 0.2s ease-out',
                }}
              >
                {HERO_EVENTS.map((event, index) => (
                  <EventCard
                    key={copy.events[index].title}
                    title={copy.events[index].title}
                    timeRange={copy.events[index].timeRange}
                    className="absolute w-[52%] min-[360px]:w-[46%] lg:w-[35%]"
                    style={{
                      [event.side]: event.offset,
                      top: event.top,
                      transform: `translateZ(${event.depth}px) rotate(${event.rotate})`,
                      backfaceVisibility: 'hidden',
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </GridItem>
      </Grid>
    </section>
  );
}
